import { useEffect, useRef, useState } from 'react';
import { cssEase } from '@savvy-wealth/savvy-kit/foundations/motion/grading';
import { SHELL_IN } from '@savvy-wealth/savvy-kit/foundations/motion/tokens';
import { cn } from '@savvy-wealth/savvy-kit/lib/utils';

import GradientSavvyLogo from '../logo/gradient-savvy-logo';

type SplashScreenProps = {
  readonly growAnimationDuration?: number;
  /** Dissolving onto the shell beneath it; the cover stays mounted until the fade has run. */
  readonly leaving?: boolean;
};

const DEFAULT_GROW_ANIMATION_DURATION = 1500;

/**
 * How long the logo stays up once shown. A warm session initialises in a frame or two, and without a floor
 * the splash appears and vanishes as a flicker.
 */
const SPLASH_MIN_MS = 1100;

/** The dissolve from the logo onto the shell is the shell's own arrival: same length, same curve. */
const SPLASH_OUT_MS = Math.round(SHELL_IN.duration * 1000);

export function SplashScreen({
  growAnimationDuration = DEFAULT_GROW_ANIMATION_DURATION,
  leaving = false,
}: SplashScreenProps) {
  return (
    <div
      data-slot="splash-screen"
      data-state={leaving ? 'leaving' : 'shown'}
      className={cn(
        'items-center bg-background flex flex-col h-screen justify-center left-0 p-6 fixed top-0 w-screen z-50 fill-mode-both',
        leaving && 'animate-out fade-out pointer-events-none',
      )}
      style={
        leaving
          ? { animationDuration: `${SPLASH_OUT_MS}ms`, animationTimingFunction: cssEase(SHELL_IN.ease) }
          : undefined
      }
    >
      <div className="w-64">
        <div
          className="animate-in fade-in-0 zoom-in-75 fill-mode-both"
          style={{ animationDuration: `${growAnimationDuration}ms` }}
        >
          <GradientSavvyLogo />
        </div>
      </div>
    </div>
  );
}

export type SplashPhase = 'shown' | 'leaving' | 'hidden';

/**
 * Where the opening splash is: up, dissolving, or gone.
 *
 * This belongs to session start, NOT to navigation: it is called once, above the router, so signing in
 * shows the logo and moving between pages never does. A page that is merely waiting on its own data shows
 * its own skeleton and then the portal's reveal sequence — replaying the logo there reads as the whole app
 * restarting.
 *
 * `hold` is every reason to keep the cover up: auth still initialising, the shell not yet knowing which
 * chrome to draw. The floor is measured from when the logo appeared, so a hold that outlasts it releases at
 * once and a hold that lasts a frame still shows the logo for the floor, never a flicker.
 */
export function useSplashPhase(hold: boolean): SplashPhase {
  const [phase, setPhase] = useState<SplashPhase>(hold ? 'shown' : 'hidden');
  const shownAt = useRef<number | null>(hold ? Date.now() : null);

  // Only re-runs when `hold` flips. A hold that stays released lets the floor timer run to completion; it is
  // never cleared by an unrelated render, which is how an earlier version left the logo up forever.
  useEffect(() => {
    if (hold) {
      if (shownAt.current === null) shownAt.current = Date.now();
      setPhase('shown');
      return;
    }
    if (shownAt.current === null) return;
    const remaining = Math.max(0, SPLASH_MIN_MS - (Date.now() - shownAt.current));
    const floor = setTimeout(() => {
      shownAt.current = null;
      setPhase('leaving');
    }, remaining);
    return () => clearTimeout(floor);
  }, [hold]);

  useEffect(() => {
    if (phase !== 'leaving') return;
    const out = setTimeout(() => setPhase('hidden'), SPLASH_OUT_MS);
    return () => clearTimeout(out);
  }, [phase]);

  return phase;
}
